import React, { useRef, useEffect } from 'react';
import { FaFacebook, FaTwitter, FaWhatsapp, FaShare } from 'react-icons/fa';
import html2canvas from 'html2canvas';

const ShareModal = ({ isOpen, onClose, post }) => {
  const shareCardRef = useRef(null);

  if (!isOpen) return null;

  const generateShareImage = async () => {
    if (!shareCardRef.current) return null;
    
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating share image:', error);
      return null;
    }
  };

  const shareToFacebook = async () => {
    const imageData = await generateShareImage();
    const shareUrl = `${window.location.origin}/dream/${post.id}`;
    
    // Create a form to submit to Facebook sharing
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://www.facebook.com/sharer/sharer.php';
    form.target = '_blank';

    const urlInput = document.createElement('input');
    urlInput.type = 'hidden';
    urlInput.name = 'u';
    urlInput.value = shareUrl;
    form.appendChild(urlInput);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const shareToTwitter = async () => {
    const imageData = await generateShareImage();
    const shareUrl = `${window.location.origin}/dream/${post.id}`;
    const cleanContent = stripFormatting(post.content);
    const text = `Check out this dream on Dreamarium!\n\n"${cleanContent.slice(0, 280)}${cleanContent.length > 280 ? '...' : ''}"\n\n`;
    
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank');
  };

  const shareToWhatsApp = async () => {
    const imageData = await generateShareImage();
    const shareUrl = `${window.location.origin}/dream/${post.id}`;
    const cleanContent = stripFormatting(post.content);
    const text = `Check out this dream on Dreamarium!\n\n"${cleanContent.slice(0, 280)}${cleanContent.length > 280 ? '...' : ''}"\n\n${shareUrl}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copyLink = async () => {
    const shareUrl = `${window.location.origin}/dream/${post.id}`;
    try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Dream link copied to clipboard!');
    } catch (err) {
        console.error('Failed to copy link:', err);
        alert('Failed to copy link. Please try again.');
    }
  };

  const shareToInstagram = async () => {
    const imageData = await generateShareImage();
    if (!imageData) return;

    // Since Instagram doesn't have a direct sharing API,
    // we'll provide instructions to save and share manually
    const blob = await (await fetch(imageData)).blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dream-share.png';
    a.click();
    window.URL.revokeObjectURL(url);

    alert('Image downloaded! Share it on Instagram:\n1. Open Instagram\n2. Create a new post\n3. Select the downloaded image\n4. Share your dream!');
  };

  // Utility function to strip HTML tags and special formatting
  const stripFormatting = (text) => {
    // Remove HTML tags
    const strippedText = text.replace(/<[^>]*>/g, '');
    
    // Remove special class tags like <span class="hashtag">
    const cleanedText = strippedText.replace(/<[^>]+class="[^"]*"[^>]*>/g, '');
    
    // Decode HTML entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanedText;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full">
        {/* Share Card Preview */}
        <div ref={shareCardRef} className="p-6 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-t-lg">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg">
            {/* App Logo and Header */}
            <div className="flex items-center mb-4">
              <img src="/logo.png" alt="Dreamarium" className="w-8 h-8 mr-2" />
              <span className="font-bold text-lg bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Dreamarium
              </span>
            </div>

            {/* Author Info */}
            <div className="flex items-center mb-4">
              <img
                src={post.authorPhotoURL || "/default-profile-pic.png"}
                alt={post.authorName}
                className="w-10 h-10 rounded-full mr-3"
              />
              <div>
                <div className="font-semibold">{post.authorName || "Anonymous"}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(post.createdAt?.toDate()).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Dream Content */}
            <p className="text-gray-800 dark:text-gray-200 mb-4 whitespace-pre-wrap">
            {stripFormatting(post.content)}
            </p>

            {/* Dream Image */}
            {post.imageUrl && (
              <img
                src={post.imageUrl}
                alt="Dream visualization"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            )}
          </div>
        </div>

        {/* Share Buttons */}
        <div className="p-4 space-y-3">
          <button
            onClick={shareToFacebook}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaFacebook /> Share on Facebook
          </button>
          
          <button
            onClick={shareToTwitter}
            className="w-full flex items-center justify-center gap-2 bg-[#1DA1F2] text-white py-2 rounded-lg hover:bg-[#1a8cd8] transition-colors"
          >
            <FaTwitter /> Share on Twitter
          </button>
          
          <button
            onClick={shareToWhatsApp}
            className="w-full flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <FaWhatsapp /> Share on WhatsApp
          </button>
          
          <button
            onClick={shareToInstagram}
            className="w-full flex items-center justify-center gap-2 bg-pink-600 text-white py-2 rounded-lg hover:bg-pink-700 transition-colors"
          >
            <FaShare /> Share on Instagram
          </button>

          <button
            onClick={copyLink}
            className="w-full flex items-center justify-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <FaShare /> Copy Link
          </button>

          <button
            onClick={onClose}
            className="w-full py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
